import { MapDataSource, MatchmakingStrategy, MatchType, PlayerGrouping } from '@nova/types';

import { assetLobbyThumbTerraforming } from './asset-bundle-names';

/** Defaults match current AGE hard-coded retry / relaxation behavior. */
const tfMapBuildPolicyDefault = {
  fallbackLayoutId: 'tf-coop-25',
  allowPadCountReduction: true,
  allowSpawnBufferRelaxation: true,
  allowZoneJitterRelaxation: true,
  layoutAttempts: 6,
  stampPlacementAttempts: 4,
  zonePartitionAttempts: 4,
  stampOriginAttempts: 80,
  fillLayoutAttempts: 8
} as const;

/** Placeholder: full-match hard cap (~2 hours). Long enough for normal play; still ends abandoned sessions. */
const TF_MATCH_TIMEOUT_MS = 2 * 60 * 60 * 1000;

/**
 * Placeholder per-cast seed spends. Flower wave is seeded lower than grass burst
 * (soft GDD intent); do not enforce that relationship in code.
 */
const TF_GRASS_BURST_COST = 20;
const TF_FLOWER_WAVE_COST = 10;

const tfGamePlayConfigCoop25 = {
  grassGoalCoverageRatio: 0.75,
  flowerExitThresholdRatio: 0.5,
  matchTimeoutMs: TF_MATCH_TIMEOUT_MS,
  obstacleTier1Threshold: 0.33,
  obstacleTier2Threshold: 0.66,
  obstructionSproutSpeedScalar: -0.05,
  grassTileSpeedScalar: 0.05,
  seedBurstPadCountMin: 3,
  seedBurstPadCountMax: 5,
  seedPodSpawnIntervalSeconds: 12,
  seedPodCap: 10,
  seedPodPayload: 5,
  grassSeedPerTileCost: 1,
  flowerSeedPerTileCost: 1,
  grassBurstCost: TF_GRASS_BURST_COST,
  flowerWaveCost: TF_FLOWER_WAVE_COST,
  seedStationRefillRadiusTiles: 2,
  seedPadMaxChargeTime: 15000,
  seedPadMinChargeTime: 5000,
  seedPadBurstRadiusPastEdgeTiles: 2,
  mapBuildPolicy: { ...tfMapBuildPolicyDefault }
} as const;

const tfGamePlayConfigCompact = {
  grassGoalCoverageRatio: 0.6,
  flowerExitThresholdRatio: 0.4,
  matchTimeoutMs: TF_MATCH_TIMEOUT_MS,
  obstacleTier1Threshold: 0.3,
  obstacleTier2Threshold: 0.6,
  obstructionSproutSpeedScalar: -0.05,
  grassTileSpeedScalar: 0.05,
  seedBurstPadCountMin: 2,
  seedBurstPadCountMax: 2,
  seedPodSpawnIntervalSeconds: 12,
  seedPodCap: 10,
  seedPodPayload: 5,
  grassSeedPerTileCost: 1,
  flowerSeedPerTileCost: 1,
  grassBurstCost: TF_GRASS_BURST_COST,
  flowerWaveCost: TF_FLOWER_WAVE_COST,
  seedStationRefillRadiusTiles: 2,
  seedPadMaxChargeTime: 15000,
  seedPadMinChargeTime: 5000,
  seedPadBurstRadiusPastEdgeTiles: 2,
  mapBuildPolicy: { ...tfMapBuildPolicyDefault }
} as const;

/**
 * Lobby / MMS / match-identity rows for Terraforming.
 * MAP geometry lives in {@link proceduralMapTfById} (keyed by `mapAssetName`); MATCH rules in `gamePlayConfig`.
 */
export const matchConfigTfLiteral = [
  {
    type: MatchType.terraforming,
    id: 'tf-coop-25',
    mapAssetName: 'tf-coop-25',
    mapDataSource: MapDataSource.Procedural,
    name: 'loc:::match_config_terraforming_coop_25_name',
    teamCount: 1,
    teamSize: 25,
    playerGrouping: PlayerGrouping.MP_COOP,
    isAvailable: true,
    matchmakingConfig: {
      strategy: MatchmakingStrategy.QUEUED_TEAM_MATCH,
      maxQueueTimeMs: 30 * 1000,
      scopePolicy: 'prefer_class_during_school_hours'
    },
    enginePrepConfig: {
      fillEmptySlotsWithAi: false
    },
    gamePlayConfig: structuredClone(tfGamePlayConfigCoop25),
    lobbyConfig: {
      title: 'loc:::game_list_terraforming_name',
      subtitle: 'loc:::match_config_terraforming_coop_25_name',
      dataKey: assetLobbyThumbTerraforming
    }
  },
  {
    type: MatchType.terraforming,
    id: 'tf-coop-compact',
    mapAssetName: 'tf-coop-compact',
    mapDataSource: MapDataSource.Procedural,
    name: 'loc:::match_config_terraforming_coop_compact_name',
    teamCount: 1,
    teamSize: 10,
    playerGrouping: PlayerGrouping.MP_COOP,
    isAvailable: false,
    matchmakingConfig: {
      strategy: MatchmakingStrategy.QUEUED_TEAM_MATCH,
      maxQueueTimeMs: 10 * 1000,
      scopePolicy: 'prefer_class_during_school_hours'
    },
    enginePrepConfig: {
      fillEmptySlotsWithAi: false
    },
    gamePlayConfig: structuredClone(tfGamePlayConfigCompact),
    lobbyConfig: {
      title: 'loc:::game_list_terraforming_name',
      subtitle: 'loc:::match_config_terraforming_coop_compact_name',
      dataKey: assetLobbyThumbTerraforming
    }
  }
] as const;
